import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import ServerSideGrid from "../../../shared/components/listview/ServerSideGrid";
import DefaultButton from "../../../shared/components/DefaultButton";
import Modalpopup from "../../../shared/components/Modalpopup";
import { Field, Form, Formik } from "formik";
import { CANCEL, SAVE } from "../../../shared/constants/Constants";
import { loadFormAllDataRequest } from "../../dynamic_form/redux/Actions";
import {
  loadFormGetByIdRequest,
  loadFormAddDataRequest,
  loadFormUpdateDataRequest,
  loadFormUpdateStatusRequest,
} from "../../asset-management/redux/Actions";

const REF_TABLE_OPTIONS = [
  { value: "store", label: "Store", refColumn: "SAP" },
  { value: "district", label: "District", refColumn: "District" },
  { value: "region", label: "Region", refColumn: "Region" },
];

const COLUMN_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "select", label: "Select" },
  { value: "option", label: "Option" },
  { value: "textarea", label: "Textarea" },
  { value: "timestamp", label: "Timestamp" },
];

const hasOptionsField = (type) => type === "select" || type === "option";

const toFieldName = (displayName) =>
  displayName.trim().toLowerCase().replace(/\s+/g, "_");

const EMPTY_NEW_COLUMN = {
  field: "",
  display_name: "",
  description: "",
  type: "text",
  options: "",
};

// Formik validate fn — mandatory description at form level + every column
const validateAddForm = (values) => {
  const errors = {};

  if (!values.description || !values.description.trim()) {
    errors.description = "Description is required";
  }

  const columnErrors = values.columns.map((col) => {
    const colErr = {};
    if (!col.description || !col.description.trim()) {
      colErr.description = "Description is required";
    }
    return colErr;
  });

  if (columnErrors.some((e) => Object.keys(e).length > 0)) {
    errors.columns = columnErrors;
  }

  return errors;
};

const FormsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const formikRef = useRef(null);

  // Selector
  const { allforms, loading } = useSelector((state) => state.forms);
  const {
    formDataById,
    loading: assetLoading,
    addSuccess,
    updateSuccess,
    statusUpdateSuccess,
  } = useSelector((state) => state.AssetManagement);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [editColumns, setEditColumns] = useState([]);
  const [formDescription, setFormDescription] = useState("");
  const [editErrors, setEditErrors] = useState({}); // { form: bool, columns: { [index]: bool } }
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    dispatch(loadFormAllDataRequest());
  }, [dispatch]);

  // Pre-populate edit columns once formDataById loads
  useEffect(() => {
    if (showEditModal && formDataById?.columns) {
      setEditColumns(
        formDataById.columns.map((col) => ({
          id: col.id,
          field: col.field,
          display_name: col.display_name,
          description: col.description ?? "",
          type: col.type,
          options: col.options || "",
          is_new: false,
          is_deleted: false,
        }))
      );
    }
  }, [formDataById, showEditModal]);

  // Close Add modal + refresh list once the add call succeeds
  useEffect(() => {
    if (addSuccess) {
      setShowAddModal(false);
      formikRef.current?.resetForm();
      dispatch(loadFormAllDataRequest());
    }
  }, [addSuccess, dispatch]);

  // Close Edit modal + refresh list once the update call succeeds
  useEffect(() => {
    if (statusUpdateSuccess) {
      setShowStatusModal(false);
      dispatch(loadFormAllDataRequest());
    }
  }, [statusUpdateSuccess, dispatch]);

  const handleAddSubmit = (values, { setSubmitting }) => {
    dispatch(loadFormAddDataRequest(values));
    setSubmitting(false);
  };

  const handleEditSubmit = () => {
    // Validate: form description + description on every non-deleted column
    const errors = { form: false, columns: {} };
    let hasError = false;

    if (!formDescription || !formDescription.trim()) {
      errors.form = true;
      hasError = true;
    }

    editColumns.forEach((col, index) => {
      if (col.is_deleted) return;
      if (!col.description || !col.description.trim()) {
        errors.columns[index] = true;
        hasError = true;
      }
    });

    setEditErrors(errors);
    if (hasError || duplicateNameIndexes.size > 0) return;

    // Top-level payload trimmed to id + name + description; columns sent as-is, unchanged.
    const payload = {
      id: selectedForm.id,
      name: selectedForm.name,
      description: formDescription,
      columns: editColumns,
    };
    dispatch(loadFormUpdateDataRequest(selectedForm.id, payload));
  };

  const handleDeleteColumn = (index) => {
    const col = editColumns[index];
    if (col.is_new) {
      // Never existed on the backend — just drop it from state entirely.
      setEditColumns(editColumns.filter((_, i) => i !== index));
    } else {
      // Existing column — soft delete so the backend can act on it.
      const updated = [...editColumns];
      updated[index] = { ...updated[index], is_deleted: true };
      setEditColumns(updated);
    }
  };

  const handleClickEdit = (row) => {
    setSelectedForm(row);
    setEditColumns([]);
    setFormDescription(row.description || "");
    setEditErrors({});
    dispatch(loadFormGetByIdRequest(row.id));
    setShowEditModal(true);
  };

  const handleClickToggleStatus = (row) => {
    setSelectedRow(row);
    setShowStatusModal(true);
  };

  const columnDefs = [
    { headerName: "ID",          field: "id",          filter: "agTextColumnFilter", sortable: true, resizable: true, width: 80, hide: true },
    { headerName: "Name",        field: "name",        filter: "agTextColumnFilter", sortable: true, resizable: true },
    { headerName: "Description", field: "description", filter: "agTextColumnFilter", sortable: true, resizable: true },
    { headerName: "Ref Table",   field: "ref_table",   filter: "agTextColumnFilter", sortable: true, resizable: true, width: 130,
      valueFormatter: (params) => REF_TABLE_OPTIONS.find(opt => opt.value === params.value)?.label || params.value },
    { headerName: "Created On",  field: "created_on",  filter: "agTextColumnFilter", sortable: true, resizable: true },
    { headerName: "",            field: "actions",     type: "actions", hide: false },
  ];

    const rowData = Array.isArray(allforms) ? allforms.map((row) => ({ ...row, status: row.is_active })) : [];
    const visibleEditColumns = editColumns.map((col, index) => ({ col, index })).filter(({ col }) => !col.is_deleted);

  // Live duplicate-name check across all non-deleted edit columns
  const duplicateNameIndexes = (() => {
    const counts = {};
    visibleEditColumns.forEach(({ col }) => {
      const name = (col.display_name || "").trim().toLowerCase();
      if (name) counts[name] = (counts[name] || 0) + 1;
    });
    const dupSet = new Set();
    visibleEditColumns.forEach(({ col, index }) => {
      const name = (col.display_name || "").trim().toLowerCase();
      if (name && counts[name] > 1) dupSet.add(index);
    });
    return dupSet;
  })();

  return (
    <>
      <Row>
        <Col xl={4} xs={12}>
          <Breadcrumbs children={[{ label: "Forms" }]} />
        </Col>
        <Col xl={8} xs={12}>
          <div className="float-end">
            <DefaultButton
              buttonClass="default-btn-color"
              buttonTitle="Add Form"
              actionBlock={() => setShowAddModal(true)}
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Card className="table-card">
          <Card.Body>
            <ServerSideGrid
              ref={gridRef}
              clientSide={true}
              columnDefs={columnDefs}
              rowData={rowData}
              loading={loading}
              paginationPageSize={20}
              tableHeight="calc(100vh - 220px)"
              defaultColDef={{ filter: "agTextColumnFilter", flex: 1, minWidth: 150 }}
              // onRowClicked={(row) => {
              //   const target = row.event.target;
              //   if (
              //       target.closest(".action-buttons") ||
              //       target.closest(".edit-btn-icon") ||
              //       target.closest(".delete-btn-icon") ||
              //       target.closest(".add-btn-icon")
              //   ) return;
              //   navigate("/asset-management-form/" + row.data["id"]);
              //   }}
              clickEdit={handleClickEdit}
              clickStatus={handleClickToggleStatus}
            />
          </Card.Body>
        </Card>
      </Row>

      {/* Add Modal */}
      <Modalpopup
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        actionTitle="Add Form"
        size="lg"
      >
        <Formik
          innerRef={formikRef}
          initialValues={{
            name: "",
            description: "",
            ref_table: "",
            ref_column: "",
            columns: [{ ...EMPTY_NEW_COLUMN }],
          }}
          validate={validateAddForm}
          onSubmit={handleAddSubmit}
        >
          {({ values, setFieldValue, isSubmitting, errors, touched }) => (
            <Form className="row g-4">
              <div className="col-md-12">
                <label className="form-label">Name <span className="error-text">*</span></label>
                <Field className="form-control" type="text" name="name" placeholder="Enter form name" autoComplete="off" />
              </div>
              <div className="col-md-12">
                <label className="form-label">Description <span className="error-text">*</span></label>
                <Field className="form-control" type="text" name="description" placeholder="Enter description" autoComplete="off" />
                {errors.description && touched.description && (
                  <div className="error-text">{errors.description}</div>
                )}
              </div>
              <div className="col-md-12">
                <label className="form-label">Ref Table <span className="error-text">*</span></label>
                <Field
                  as="select"
                  className="form-control"
                  name="ref_table"
                  onChange={(e) => {
                    const selected = REF_TABLE_OPTIONS.find((opt) => opt.value === e.target.value);
                    setFieldValue("ref_table", e.target.value);
                    setFieldValue("ref_column", selected ? selected.refColumn : "");
                  }}
                >
                  <option value="">Select Ref Table</option>
                  {REF_TABLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Field>
              </div>
              <div className="col-md-12">
                <label className="form-label">Ref Column <span className="error-text">*</span></label>
                <Field className="form-control" type="text" name="ref_column" placeholder="Auto-filled based on Ref Table" disabled />
              </div>

              <div className="col-md-12">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">Columns</label>
                  <DefaultButton
                    buttonClass="btn btn-sm default-btn-color"
                    buttonTitle={<><i className="fa fa-plus me-1" /> Add Column</>}
                    actionBlock={() =>
                      setFieldValue("columns", [
                        ...values.columns,
                        { ...EMPTY_NEW_COLUMN },
                      ])
                    }
                  />
                </div>
                {values.columns.map((col, index) => (
                  <div
                    key={index}
                    className="d-flex gap-2 align-items-start"
                    style={{
                      flexWrap: "nowrap",
                      overflowX: "auto",
                      padding: "10px 8px",
                      borderBottom: "1px solid #e5e7eb",
                      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                    }}
                  >
                    <Field
                      className="form-control"
                      style={{ flex: "1 1 0px", minWidth: "110px" }}
                      name={`columns[${index}].display_name`}
                      placeholder="Display name"
                      onChange={(e) => {
                        const displayName = e.target.value;
                        const fieldName = toFieldName(displayName);
                        setFieldValue(`columns[${index}].display_name`, displayName);
                        setFieldValue(`columns[${index}].field`, fieldName);
                      }}
                    />
                    <Field
                      className="form-control"
                      style={{ flex: "1 1 0px", minWidth: "110px" }}
                      name={`columns[${index}].field`}
                      placeholder="Field"
                      disabled
                    />
                    <div style={{ flex: "1 1 0px", minWidth: "110px" }}>
                      <Field
                        className="form-control"
                        name={`columns[${index}].description`}
                        placeholder="Description *"
                      />
                      {errors.columns?.[index]?.description && touched.columns?.[index]?.description && (
                        <div className="error-text">{errors.columns[index].description}</div>
                      )}
                    </div>
                    <Field
                      as="select"
                      className="form-control"
                      style={{ flex: "1 1 0px", minWidth: "110px" }}
                      name={`columns[${index}].type`}
                    >
                      {COLUMN_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Field>
                    {hasOptionsField(values.columns[index].type) && (
                      <Field
                        className="form-control"
                        style={{ flex: "1 1 0px", minWidth: "110px" }}
                        name={`columns[${index}].options`}
                        placeholder="Enter values comma separated"
                      />
                    )}
                    {values.columns.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        style={{ flexShrink: 0 }}
                        onClick={() =>
                          setFieldValue("columns", values.columns.filter((_, i) => i !== index))
                        }
                      >
                        <i className="fa fa-trash-alt" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="col-md-12">
                <DefaultButton buttonClass="btn btn-secondary me-2" buttonTitle={CANCEL} actionBlock={() => setShowAddModal(false)} />
                <DefaultButton buttonClass="default-btn-color" buttonTitle={SAVE} buttonType="submit" disabled={isSubmitting} />
              </div>
            </Form>
          )}
        </Formik>
      </Modalpopup>

      {/* Edit Modal */}
      <Modalpopup
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        actionTitle={`Edit Columns — ${selectedForm?.name || ""}`}
        size="lg"
      >
        <div className="row g-4">
          <div className="col-md-12">
            <label className="form-label">Description <span className="error-text">*</span></label>
            <input
              className="form-control"
              placeholder="Enter description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
            />
            {editErrors.form && (
              <div className="error-text">Description is required</div>
            )}
          </div>

          <div className="col-md-12">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label mb-0">Columns</label>
              <DefaultButton
                buttonClass="btn btn-sm default-btn-color"
                buttonTitle={<><i className="fa fa-plus me-1" /> Add Column</>}
                actionBlock={() =>
                  setEditColumns([
                    ...editColumns,
                    { id: null, ...EMPTY_NEW_COLUMN, is_new: true, is_deleted: false },
                  ])
                }
              />
            </div>
            {assetLoading ? (
              <p>Loading columns...</p>
            ) : visibleEditColumns.length === 0 ? (
              <p className="text-muted">No columns defined.</p>
            ) : (
              visibleEditColumns.map(({ col, index }) => (
                <div
                  key={index}
                  className="d-flex gap-2 align-items-start"
                  style={{
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    padding: "10px 8px",
                    borderBottom: "1px solid #e5e7eb",
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                  }}
                >
                  <div style={{ flex: "1 1 0px", minWidth: "110px" }}>
                    <input
                      className="form-control"
                      placeholder="Display name"
                      value={col.display_name}
                      onChange={(e) => {
                        const displayName = e.target.value;
                        const fieldName = toFieldName(displayName);
                        const updated = [...editColumns];
                        updated[index] = {
                          ...updated[index],
                          display_name: displayName,
                          field: fieldName,
                        };
                        setEditColumns(updated);
                      }}
                    />
                    {duplicateNameIndexes.has(index) && (
                      <div className="error-text">Duplicate Column Name</div>
                    )}
                  </div>
                  <input
                    className="form-control"
                    style={{ flex: "1 1 0px", minWidth: "110px" }}
                    placeholder="Field"
                    value={col.field}
                    disabled
                  />
                  <div style={{ flex: "1 1 0px", minWidth: "110px" }}>
                    <input
                      className="form-control"
                      placeholder="Description *"
                      value={col.description}
                      onChange={(e) => {
                        const updated = [...editColumns];
                        updated[index] = { ...updated[index], description: e.target.value };
                        setEditColumns(updated);
                      }}
                    />
                    {editErrors.columns?.[index] && (
                      <div className="error-text">Description is required</div>
                    )}
                  </div>
                  <div style={{ flex: "1 1 0px", minWidth: "110px", position: "relative" }}>
                    <select
                      className="form-control"
                      style={col.is_new ? {} : { paddingRight: "28px", cursor: "not-allowed", backgroundColor: "#eceff1" }}
                      value={col.type}
                      disabled={!col.is_new}
                      title={col.is_new ? undefined : "Type can't be changed after creation"}
                      onChange={(e) => {
                        const updated = [...editColumns];
                        updated[index] = { ...updated[index], type: e.target.value };
                        setEditColumns(updated);
                      }}
                    >
                      {COLUMN_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {!col.is_new && (
                      <i
                        className="fa fa-lock"
                        title="Type can't be changed after creation"
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "12px",
                          color: "#78909c",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                  </div>
                  {hasOptionsField(col.type) && (
                    <input
                      className="form-control"
                      style={{ flex: "1 1 0px", minWidth: "110px" }}
                      placeholder="Enter values comma separated"
                      value={col.options}
                      onChange={(e) => {
                        const updated = [...editColumns];
                        updated[index] = { ...updated[index], options: e.target.value };
                        setEditColumns(updated);
                      }}
                    />
                  )}
                  {visibleEditColumns.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      style={{ flexShrink: 0 }}
                      onClick={() => handleDeleteColumn(index)}
                    >
                      <i className="fa fa-trash-alt" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          <div className="col-md-12">
            <DefaultButton buttonClass="btn btn-secondary me-2" buttonTitle={CANCEL} actionBlock={() => setShowEditModal(false)} />
            <DefaultButton buttonClass="default-btn-color" buttonTitle={SAVE} actionBlock={handleEditSubmit} />
          </div>
        </div>
      </Modalpopup>
      <Modalpopup
          show={showStatusModal}
          handleClose={() => setShowStatusModal(false)}
          actionTitle="Form Status"
        >
          <p>
            {selectedRow?.is_active === true ? (
              <>Do you want to <strong>Disable</strong> <strong>{selectedRow?.name}</strong>?</>
            ) : (
              <>Do you want to <strong>Enable</strong> <strong>{selectedRow?.name}</strong>?</>
            )}
          </p>

          <div className="d-flex justify-content-end gap-2">
            <DefaultButton
              buttonClass="btn btn-secondary"
              buttonTitle="Cancel"
              actionBlock={() => setShowStatusModal(false)}
            />
            <DefaultButton
              buttonClass={selectedRow?.is_active === true ? "btn btn-danger" : "btn btn-success"}
              buttonTitle={selectedRow?.is_active === true ? "Disable" : "Enable"}
              actionBlock={() => {
                dispatch(loadFormUpdateStatusRequest(selectedRow.id, {
                  id: selectedRow.id,
                  is_active: !selectedRow.is_active,
                }));
              }}
            />
          </div>
        </Modalpopup>
    </>
  );
};

export default FormsList;